// src/pages/financials/OverviewTab.tsx
//
// Financials Overview — KPI tiles + monthly revenue/net-income chart from
// qbd-pnl-summary, plus an expense breakdown (qbd-expense-breakdown) for
// the same period. Month-count pills (3/6/12) drive both queries; the
// expense breakdown's dateFrom/dateTo are computed client-side as the first
// day of the earliest month → today.
//
// Month labels from the recipe are 'yyyy-MM' strings — rendered as-is.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LayoutDashboard, RefreshCw, AlertTriangle } from 'lucide-react';
import {
  ComposedChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, ReferenceLine,
} from 'recharts';
import { apiV2 as api } from '../../services/apiV2';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { KpiPanel } from './KpiPanel';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtPct = (v: number) => `${v.toFixed(1)}%`;

const MONTH_OPTIONS = [3, 6, 12];

const pad = (n: number) => String(n).padStart(2, '0');
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
/** First day of the month `months - 1` months before the current month. */
const firstOfMonthsBack = (months: number) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - (months - 1));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
};

export function OverviewTab() {
  const [months, setMonths] = useState(12);
  const { canSee } = useFeatureAccess();
  const dateFrom = firstOfMonthsBack(months);
  const dateTo = todayStr();

  const summaryQ = useQuery({
    queryKey: ['qbd', 'pnl-summary', months],
    queryFn: () => api.getQbdPnlSummary({ months }),
    staleTime: 60_000,
    retry: 1,
  });

  const expenseQ = useQuery({
    queryKey: ['qbd', 'expense-breakdown', dateFrom, dateTo],
    queryFn: () => api.getQbdExpenseBreakdown({ dateFrom, dateTo }),
    staleTime: 60_000,
    retry: 1,
  });

  const totals = summaryQ.data?.totals;
  const buckets = summaryQ.data?.buckets ?? [];
  const expenses = expenseQ.data?.rows ?? [];
  const maxExpense = expenses.reduce((m, r) => Math.max(m, r.amount), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <LayoutDashboard size={20} /> Overview
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            Revenue, profitability, and expense mix from QuickBooks Desktop.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            {MONTH_OPTIONS.map((m) => (
              <button key={m} onClick={() => setMonths(m)}
                className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                  months === m
                    ? 'bg-indigo-500/15 text-indigo-200 border-indigo-500/40'
                    : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                }`}>
                {m} months
              </button>
            ))}
          </div>
          <button onClick={() => { summaryQ.refetch(); expenseQ.refetch(); }} disabled={summaryQ.isLoading}
            className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
            <RefreshCw size={14} className={summaryQ.isFetching || expenseQ.isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {summaryQ.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load the P&L summary</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(summaryQ.error as Error).message}</div>
          </div>
        </div>
      )}

      {summaryQ.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !summaryQ.error && (
        <>
          {/* KPI tiles */}
          {totals && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">Revenue</p>
                <p className="text-lg font-bold text-white">{fmtCur(totals.revenue)}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">Gross Profit</p>
                <p className="text-lg font-bold text-white">{fmtCur(totals.grossProfit)}</p>
                <p className="text-[11px] text-slate-500">{fmtPct(totals.grossMarginPct)} margin</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">Expenses</p>
                <p className="text-lg font-bold text-white">{fmtCur(totals.expenses)}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">Net Income</p>
                <p className={`text-lg font-bold ${totals.netIncome < 0 ? 'text-red-400' : 'text-white'}`}>{fmtCur(totals.netIncome)}</p>
                <p className={`text-[11px] ${totals.netIncome < 0 ? 'text-red-400/70' : 'text-slate-500'}`}>{fmtPct(totals.netMarginPct)} margin</p>
              </div>
            </div>
          )}

          {/* Revenue vs Net Income by month */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Revenue & Net Income by month</h3>
            {buckets.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">No activity in the selected period</div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={buckets} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                    <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      interval="preserveStartEnd" minTickGap={24} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      tickFormatter={(v) => `$${(Number(v) / 1000).toFixed(0)}k`} />
                    <Tooltip
                      cursor={{ fill: '#475569', fillOpacity: 0.15 }}
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                      formatter={(value, name) => [fmtCur(Number(value)), name === 'revenue' ? 'Revenue' : 'Net Income']}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }}
                      formatter={(v) => v === 'revenue' ? 'Revenue' : 'Net Income'} />
                    <ReferenceLine y={0} stroke="#475569" />
                    <Bar dataKey="revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={32} />
                    <Bar dataKey="netIncome" radius={[4, 4, 0, 0]} maxBarSize={32}>
                      {buckets.map((b) => (
                        <Cell key={b.month} fill={b.netIncome < 0 ? '#ef4444' : '#10b981'} />
                      ))}
                    </Bar>
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}

      {/* Expense breakdown for the same period */}
      <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-semibold text-white">Expense breakdown</h3>
          <span className="text-[11px] text-slate-500">{dateFrom} → {dateTo}</span>
        </div>
        {expenseQ.error ? (
          <div className="p-3 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
            <AlertTriangle size={14} />
            <span className="text-xs">Couldn't load the expense breakdown: {(expenseQ.error as Error).message}</span>
          </div>
        ) : expenseQ.isLoading ? (
          <div className="flex items-center justify-center h-24"><RefreshCw className="w-5 h-5 text-blue-400 animate-spin" /></div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-6 text-slate-500 text-sm">No expenses in the selected period</div>
        ) : (
          <div className="space-y-1.5">
            {expenses.map((r) => (
              <div key={r.account} className="flex items-center gap-3 text-xs">
                <div className="w-44 sm:w-56 shrink-0 truncate text-slate-300" title={r.account}>{r.account}</div>
                <div className="flex-1 h-4 rounded bg-slate-900/50 overflow-hidden">
                  <div
                    className="h-full rounded bg-indigo-500/60"
                    style={{ width: `${maxExpense > 0 ? Math.max(1, (r.amount / maxExpense) * 100) : 0}%` }}
                  />
                </div>
                <div className="w-20 shrink-0 text-right text-slate-300 tabular-nums">{fmtCur(r.amount)}</div>
                <div className="w-12 shrink-0 text-right text-slate-500 tabular-nums">{fmtPct(r.pct)}</div>
              </div>
            ))}
            <div className="flex items-center gap-3 text-xs pt-1.5 border-t border-slate-700/40">
              <div className="w-44 sm:w-56 shrink-0 font-semibold text-white">Total expenses</div>
              <div className="flex-1" />
              <div className="w-20 shrink-0 text-right font-semibold text-white tabular-nums">{fmtCur(expenseQ.data?.total ?? 0)}</div>
              <div className="w-12 shrink-0" />
            </div>
          </div>
        )}
      </div>

      {canSee('financials.kpipanel') && <KpiPanel />}
    </div>
  );
}
